import React from 'react';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { mdiCalendarToday } from '@mdi/js';
import Icon from '@mdi/react';
import WeekNavigator from '../common/week_navigator';
import {
  currentViewWeekStartDateStr,
  dateMinusDays,
  daysArrayIndex,
  dbWeekStartDatesAndDayIndexesCoveringCalendarRange,
  mealIDsCandidatesForAddingToMealBasket,
} from '../../services/planner';
import PlannerWeekActionsPopupMenu from './planner_week_actions_popup_menu';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  clearPlanner,
  getOrCreatePlannerWeeks,
  plannerCopyPreviousWeekMeals,
} from '../../operations/planner_operations';
import {
  useCreateContentEntryMutation,
  useCreateMealMutation,
  useCreateNoteMutation,
  useDeleteContentEntryMutation,
  useDeleteMealMutation,
  useDeleteNoteMutation,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useLazyGetContentEntriesByParentQuery,
  useLazyGetDbPlannerWeekQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullMealsByParentQuery,
  useLazyGetNotesByParentQuery,
  useUpdateDbPlannerWeekMutation,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { deduplicate } from '../../services/arrays';
import { mealsAddedToMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import { mergeMyDayActionRecords } from '../../services/programmes';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import { mealWithScaledIngredientsReplaced } from '../../services/meals';
import { scaleMealIngredientsOperation } from '../../operations/meal_operations';

const TopNavBarMobilePlanner = ({ plannerViewWeekStartDate }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const { data: currentProgrammeEnrollment } =
    useCurrentProgrammeEnrollmentQuery();

  const plannerViewWeekStartDay = 1; /* plannerViewWeekStartDayOrDefault(
    userProfile?.plannerViewWeekStartDay,
  ); */

  const thisWeekStartDate = currentViewWeekStartDateStr(
    plannerViewWeekStartDay,
  );
  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekMutation();

  const { data: myDayActionRecordsByEnrollment } =
    useGetMyDayActionRecordsByEnrollmentQuery(currentProgrammeEnrollment?.id, {
      skip: !currentProgrammeEnrollment?.id,
    });

  const { data: myDayActionRecordsBySpaceMembership } =
    useGetMyDayActionRecordsBySpaceMembershipQuery(currentSpaceMembership?.id, {
      skip: !currentSpaceMembership?.id,
    });

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const [lazyGetDbPlannerWeek] = useLazyGetDbPlannerWeekQuery();

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetFullMealsByParent] = useLazyGetFullMealsByParentQuery();

  const [lazyGetNotesByParent] = useLazyGetNotesByParentQuery();

  const [lazyGetContentEntriesByParent] =
    useLazyGetContentEntriesByParentQuery();

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [createNote] = useCreateNoteMutation();

  const [createContentEntry] = useCreateContentEntryMutation();

  return (
    <div className="topNavBarMobile">
      <div style={{ margin: 'auto 10px auto 20px' }}>
        <Icon
          path={mdiCalendarToday}
          onClick={() => navigate(`/planner/${thisWeekStartDate}`)}
          style={{
            width: '36px',
            height: '36px',
            color: 'var(--app-primary-font-color)',
            padding: '0',
            marginBottom: -5,
          }}
        />
      </div>
      <div
        style={{
          margin: 'auto',
          color: 'var(--app-primary-font-color)',
          fontSize: '16px',
        }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            fontSize: 18,
            fontWeight: 'bold',
            margin: 'auto 0',
          }}>
          <WeekNavigator
            startDate={plannerViewWeekStartDate}
            onChangeStartDate={(startDate) => navigate(`/planner/${startDate}`)}
            innerWeekWidth={190}
            arrowStyle={{ color: 'var(--app-primary-font-color)' }}
          />
        </div>
      </div>
      <div style={{ margin: 'auto 10px' }}>
        <PlannerWeekActionsPopupMenu
          onDeleteAllMeals={() => {
            const deleteUntilDate = dayjs(plannerViewWeekStartDate)
              .add(7, 'day')
              .toISOString();
            clearPlanner(
              plannerViewWeekStartDate,
              deleteUntilDate,
              lazyGetOrCreateDbPlannerWeekByStartDate,
              dispatch,
              deleteMeal,
              deleteNote,
              deleteContentEntry,
              updateDbPlannerWeek,
            );
          }}
          onAddWeekMealsToMealsBasket={async (scaleToServings) => {
            const startOfNextWeek = dayjs(plannerViewWeekStartDate)
              .add(7, 'day')
              .toISOString();
            const dbWeekRecords =
              dbWeekStartDatesAndDayIndexesCoveringCalendarRange(
                plannerViewWeekStartDate,
                startOfNextWeek,
              );
            const dbWeekStartDates = deduplicate(
              dbWeekRecords.map((dbWeek) => dbWeek.dbWeekStartDate),
            );
            const plannerWeeks = await getOrCreatePlannerWeeks(
              dbWeekStartDates,
              lazyGetOrCreateDbPlannerWeekByStartDate,
            );
            const mealIDsToAddToBasket = [];
            dbWeekRecords.forEach((dbWeekRecord) => {
              const plannerWeek = plannerWeeks[dbWeekRecord.dbWeekStartDate];
              if (plannerWeek) {
                const dayIndexStr = dbWeekRecord.dayIndex.toString();
                const plannerDay =
                  plannerWeek.days[daysArrayIndex(dayIndexStr)];
                const mealIDs = mealIDsCandidatesForAddingToMealBasket(
                  plannerDay,
                  myDayActionRecords,
                );
                mealIDs.filter(Boolean).forEach((mealID) => {
                  mealIDsToAddToBasket.push(mealID);
                });
              }
            });
            const fullMealsToAddToBasket = await Promise.all(
              mealIDsToAddToBasket.map(async (mealID) => {
                const { data: fullMeal } = await lazyGetMeal(mealID);
                return fullMeal;
              }),
            );
            const scaledMealsToAddToBasket = await Promise.all(
              fullMealsToAddToBasket.map(async (fullMeal) => {
                if (!scaleToServings) {
                  return fullMeal;
                }
                const scaleFactor =
                  scaleToServings / (fullMeal.recipes[0]?.servings || 1);
                const scaledIngredients = await scaleMealIngredientsOperation(
                  fullMeal,
                  scaleFactor,
                  customerFoodDataID,
                );
                return mealWithScaledIngredientsReplaced(
                  fullMeal,
                  scaleToServings,
                  scaledIngredients,
                  scaleFactor,
                );
              }),
            );
            dispatch(mealsAddedToMealBasket(scaledMealsToAddToBasket));
          }}
          onCopyPreviousWeekMeals={async () => {
            return plannerCopyPreviousWeekMeals(
              plannerViewWeekStartDate,
              dateMinusDays(plannerViewWeekStartDate, 7),
              lazyGetDbPlannerWeek,
              lazyGetFullMealsByParent,
              lazyGetNotesByParent,
              lazyGetContentEntriesByParent,
              createMeal,
              createNote,
              createContentEntry,
              updateMeal,
              updateDbPlannerWeek,
              currentSpaceMembership,
              customerFoodDataID,
            );
          }}
        />
      </div>
    </div>
  );
};

TopNavBarMobilePlanner.propTypes = {
  plannerViewWeekStartDate: PropTypes.string.isRequired,
};

export default TopNavBarMobilePlanner;
